import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '../../lib/api-client';
import { useRegister } from '../../lib/api';
import { AuthLayout } from './auth-layout';
import { ProviderSignIn } from '../../components/oauth-buttons';
import { Button, Field, FormError, TextInput } from '../../components/ui';

/**
 * Mirrors the Platform API's rule: length is what matters, so a 12-character minimum with no
 * composition requirements. Validating here as well gives immediate feedback; the server remains
 * the authority.
 */
const schema = z.object({
  email: z.email('Enter a valid email address.'),
  password: z.string().min(12, 'Use at least 12 characters.').max(200, 'Too long.'),
  displayName: z.string().trim().max(100).optional(),
});

type RegisterForm = z.infer<typeof schema>;

export function RegisterRoute() {
  const registerAccount = useRegister();
  const [formError, setFormError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit((values) => {
    setFormError(null);
    // React Hook Form yields an empty string for an untouched optional field. Sending it would be
    // rejected: the server treats a supplied display name as a value that must be non-empty.
    // Omitting the key is what "not provided" actually means.
    const displayName = values.displayName?.trim();
    const payload = {
      email: values.email,
      password: values.password,
      ...(displayName === undefined || displayName === '' ? {} : { displayName }),
    };
    registerAccount.mutate(payload, {
      onSuccess: () => setSubmittedEmail(values.email),
      onError: (error) => {
        setFormError(error instanceof ApiError ? error.message : 'Could not create the account.');
      },
    });
  });

  if (submittedEmail !== null) {
    return (
      <AuthLayout
        title="Check your email"
        description="If that address can receive mail, a verification link is on its way."
        footer={
          <Link to="/login" className="link">
            Back to sign in
          </Link>
        }
      >
        {/* The confirmation is deliberately identical whether or not the address was already
            registered, so this screen cannot be used to test whether an account exists. */}
        <p className="text-sm text-neutral-700" data-testid="register-confirmation">
          We sent a verification link to <span className="font-medium">{submittedEmail}</span>. Open
          it to finish setting up your account.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      description="Start with the Free plan. No card required."
      footer={
        <span>
          Already have an account?{' '}
          <Link to="/login" className="link">
            Sign in
          </Link>
        </span>
      }
    >
      <div className="flex flex-col gap-4">
        {/* The same button signs in and signs up; an account it creates has no password. */}
        <ProviderSignIn />

        <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
          <FormError message={formError} />

          <Field label="Email" htmlFor="email" error={errors.email?.message}>
            <TextInput
              id="email"
              type="email"
              autoComplete="email"
              invalid={errors.email !== undefined}
              {...register('email')}
            />
          </Field>

          <Field
            label="Password"
            htmlFor="password"
            hint="At least 12 characters. A passphrase works well."
            error={errors.password?.message}
          >
            <TextInput
              id="password"
              type="password"
              autoComplete="new-password"
              invalid={errors.password !== undefined}
              {...register('password')}
            />
          </Field>

          <Field
            label="Name (optional)"
            htmlFor="displayName"
            hint="Used to greet you. You can change it later in Settings."
            error={errors.displayName?.message}
          >
            <TextInput id="displayName" autoComplete="name" {...register('displayName')} />
          </Field>

          <Button type="submit" disabled={isSubmitting || registerAccount.isPending}>
            {registerAccount.isPending ? 'Creating account...' : 'Create account'}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
